// One agent's standing on one project, in the agent pane: opened from the agent's name on
// a next action or an answer, so the operator can see the project from that agent's side
// without leaving the list or the conversation. A compact read of the agent's own page:
// its status headline, the block that agent keeps (Grid's applications and options,
// Legal's contracts, Building readiness's areas), its Ready to build checks, then its
// actions, dates and history. See docs/Y2026-Q4/design-agent-peek.md.
import { useMemo } from "react";
import { Link } from "react-router-dom";

import type { PortfolioRow } from "../api/hooks/useAgentPortfolio";
import {
  type ProjectAgentStatus,
  type ProjectAgentStatusList,
  useProjectAgentStatus,
} from "../api/hooks/useProjectAgentStatus";
import { projectSpecialistPath } from "../routes/learningAgentPaths";
import { type PanePeek, useAgentPane, usePaneProjectName } from "../state/useAgentPane";
import { projectSource } from "../utils/nextActions";
import { areaLines } from "../utils/readinessAreas";
import { agentDisplayLabel } from "../utils/runParticipants";
import { statusOpener } from "../utils/statusOpener";
import { ContractsCard } from "./ContractsCard";
import { HistoryCard } from "./HistoryCard";
import { ActionTable } from "./NextActionTable";
import {
  Applications,
  OptionsOnTheTable,
  StatusForCard,
  VerdictReason,
  resolveVerdictItem,
} from "./PortfolioRowParts";
import { ReadyToBuildBody, ReadyToBuildCount } from "./ReadyToBuild";
import { SectionBlock, SectionCard } from "./SpecialistRowSections";
import { StatusCardChips } from "./StatusCardParts";
import { DateTable } from "./TrackedDateTable";
import { useCitations } from "./useCitations";
import frame from "./AgentPaneConversation.module.css";
import styles from "./AgentPeek.module.css";

// The block this agent keeps beyond its status, as its own page shows it.
const KeyBlock = ({
  entry,
  row,
  projectId,
}: {
  entry: ProjectAgentStatus;
  row: PortfolioRow;
  projectId: string;
}) => (
  <>
    {entry.keeps_applications && (
      <SectionCard title="Applications" aside={null}>
        <SectionBlock>
          <Applications applications={row.applications} />
        </SectionBlock>
      </SectionCard>
    )}
    {(entry.keeps_applications || row.options.trim()) && (
      <SectionCard title="Options on the table" aside={null}>
        <SectionBlock>
          <OptionsOnTheTable text={row.options} />
        </SectionBlock>
      </SectionCard>
    )}
    {entry.keeps_contracts && <ContractsCard projectId={projectId} />}
    {entry.keeps_readiness && row.readiness && (
      <SectionCard title="Areas" aside={null}>
        <SectionBlock>
          <StatusForCard text={areaLines(row.readiness)} omitLabels={[]} />
        </SectionBlock>
      </SectionCard>
    )}
  </>
);

const PeekBody = ({
  entry,
  data,
  projectId,
}: {
  entry: ProjectAgentStatus;
  data: ProjectAgentStatusList;
  projectId: string;
}) => {
  const citations = useCitations(useMemo(() => entry.timeline, [entry]));
  const row = entry.row;
  const lists = { data, agent: entry.agent, projectId };
  return (
    <>
      {citations.modal}
      {row ? (
        <>
          <SectionCard
            title="Status"
            aside={<StatusCardChips row={row} lists={lists} />}
            asideAt="start"
          >
            {row.on_track?.verdict && (
              <SectionBlock>
                <VerdictReason
                  onTrack={row.on_track}
                  found={resolveVerdictItem(row.on_track, lists)}
                />
              </SectionBlock>
            )}
            <SectionBlock>
              <StatusForCard text={statusOpener(row.status_details)} omitLabels={[]} />
            </SectionBlock>
          </SectionCard>
          <KeyBlock entry={entry} row={row} projectId={projectId} />
        </>
      ) : (
        <p className={frame.muted}>
          {entry.label} is switched on but has not written a status yet.
        </p>
      )}
      <SectionCard
        title="Ready to build"
        aside={<ReadyToBuildCount agent={entry.agent} data={data} />}
        folded={{ open: false }}
      >
        <SectionBlock>
          <ReadyToBuildBody agent={entry.agent} projectId={projectId} data={data} />
        </SectionBlock>
      </SectionCard>
      <SectionCard title="Next actions" aside={null}>
        <SectionBlock>
          <ActionTable
            scope={{ kind: "agent", agent: entry.agent }}
            sources={[projectSource(projectId, data)]}
            empty="No next actions recorded."
            controls={false}
            showArea={entry.keeps_readiness}
            anchored={false}
          />
        </SectionBlock>
      </SectionCard>
      <SectionCard title="Tracked dates" aside={null}>
        <SectionBlock>
          <DateTable
            agent={entry.agent}
            projectId={projectId}
            data={data}
            empty="No tracked dates recorded."
            collapseCovered
            showArea={entry.keeps_readiness}
          />
        </SectionBlock>
      </SectionCard>
      {/* Building readiness keeps each area's history in the area; its file's holds only
          what falls outside them, shown once there is any, as on its page. */}
      {entry.keeps_readiness ? (
        entry.timeline.length > 0 && (
          <HistoryCard
            title="History outside the three areas"
            entries={entry.timeline}
            citations={citations}
          />
        )
      ) : (
        <HistoryCard title="History" entries={entry.timeline} citations={citations} />
      )}
    </>
  );
};

export const AgentPeek = ({
  peek,
  hidden,
}: {
  peek: PanePeek;
  /** Another tab is in front. */
  hidden: boolean;
}) => {
  const pane = useAgentPane();
  const projectName = usePaneProjectName(peek.projectId);
  const { data, isLoading, error } = useProjectAgentStatus(peek.projectId);
  const entry = data?.agents.find((a) => a.agent === peek.agent);
  const label = entry?.label ?? agentDisplayLabel(peek.agent);
  return (
    <section className={frame.conversation} hidden={hidden}>
      <header className={frame.head}>
        <div className={frame.headTop}>
          <nav className={frame.crumb} aria-label="Where this is from">
            <span className={frame.project}>{projectName}</span>
            <span aria-hidden>›</span>
            <span>{label}</span>
          </nav>
          <span className={frame.spacer} />
          <span className={frame.headActions}>
            <Link
              className={frame.link}
              to={projectSpecialistPath(peek.projectId, peek.agent)}
            >
              Open {label}'s page
            </Link>
            <button
              type="button"
              className={styles.close}
              onClick={() => pane.close(peek.key)}
              aria-label={`Close where ${label} stands`}
              title="Close"
            >
              ×
            </button>
          </span>
        </div>
        <h2 className={frame.title}>
          Where {label} stands on {projectName}
        </h2>
      </header>
      <div className={`${frame.body} ${styles.body}`}>
        {isLoading ? (
          <p className={frame.muted}>Loading…</p>
        ) : error ? (
          <p className={frame.error}>Could not load {label}'s file.</p>
        ) : !data || !entry ? (
          <p className={frame.muted}>{label} does not work this project.</p>
        ) : (
          <PeekBody entry={entry} data={data} projectId={peek.projectId} />
        )}
      </div>
    </section>
  );
};
